import React, { useState } from 'react';
import { Button } from 'ui'
import CloseIcon from '@mui/icons-material/Close';

import ColorConstants from 'constants/ColorConstants'
import { v1 as uuidv1 } from 'uuid';

const createNewChoice = () => ({id: uuidv1(), choice: ""});

const choicesByID = (choices) => {
  return choices.reduce((accumulator, choice) => {
    accumulator[choice.id] = Object.assign({}, choice);
    return accumulator;
  }, {});
};

// Edits are staged entirely in editor state, snapshotted from props at mount.
// Submit diffs the working copy against the pristine snapshot, so props
// updating mid-edit (refetches, collaborator changes) can never desync from
// what the user is editing. Parents must render this component with a
// descriptor-identity key so switching descriptors remounts a fresh snapshot.
const initialEditorState = (descriptor) => {
  const multi_choices = (descriptor && descriptor.multi_choices) || [];
  const newChoice = descriptor ? null : createNewChoice();

  return {
    currentChoicesIndices: multi_choices.map((choice) => choice.id),
    currentChoices: choicesByID(multi_choices),
    originalChoices: choicesByID(multi_choices),
    newChoicesIndices: newChoice ? [newChoice.id] : [],
    newChoices: newChoice ? {[newChoice.id]: newChoice} : {},
    deleteChoices: [],
    descriptorName: (descriptor && descriptor.name) || "",
    originalDescriptorName: (descriptor && descriptor.name) || ""
  };
};

function EditDescriptorComponent({descriptor, doneEditingCallback, deleteCallback, disabled}) {
  const descriptorID = descriptor ? descriptor.id : null;

  const [editor, setEditor] = useState(() => initialEditorState(descriptor));

  const addChoice = () => {
    const newChoice = createNewChoice();
    setEditor((editor) => ({
      ...editor,
      newChoices: {...editor.newChoices, [newChoice.id]: newChoice},
      newChoicesIndices: editor.newChoicesIndices.concat(newChoice.id)
    }));
  };

  const deleteClicked = (e) => {
    e.preventDefault();
    deleteCallback(descriptorID);
  };

  const doneClicked = (e) => {
    e.preventDefault();

    const deletePayload = editor.deleteChoices.map((choiceID) => {
      return {id: choiceID}
    });

    const updatePayload = editor.currentChoicesIndices.filter((choiceID) => {
      const current = editor.currentChoices[choiceID];
      const original = editor.originalChoices[choiceID];
      return !editor.deleteChoices.includes(choiceID) &&
                current.choice != original.choice &&
                current.choice.length > 0;
    }).map((choiceID) => {
      return editor.currentChoices[choiceID];
    });

    const newPayload = editor.newChoicesIndices.map((choiceID) => {
      return editor.newChoices[choiceID];
    }).filter((choice) => {return choice.choice.length > 0});

    const name = editor.descriptorName != editor.originalDescriptorName ? editor.descriptorName : null;

    doneEditingCallback(descriptorID,
                          name,
                          newPayload,
                          updatePayload,
                          deletePayload
                        );
  };

  const updateNewChoiceString = (e, choiceID) => {
    const updatedChoice = {...editor.newChoices[choiceID], choice: e.target.value};
    setEditor((editor) => ({
      ...editor,
      newChoices: {...editor.newChoices, [choiceID]: updatedChoice}
    }));
  };

  const deleteNewChoice = (e, choiceID) => {
    setEditor((editor) => {
      const newChoices = {...editor.newChoices};
      delete newChoices[choiceID];
      return {
        ...editor,
        newChoices: newChoices,
        newChoicesIndices: editor.newChoicesIndices.filter((newChoiceID) => newChoiceID != choiceID)
      };
    });
  };

  const deleteChoice = (e, choiceID) => {
    setEditor((editor) => ({
      ...editor,
      deleteChoices: editor.deleteChoices.concat(choiceID)
    }));
  };

  const updateCurrentChoiceString = (e, choiceID) => {
    const updatedChoice = {...editor.currentChoices[choiceID], choice: e.target.value};
    setEditor((editor) => ({
      ...editor,
      currentChoices: {...editor.currentChoices, [choiceID]: updatedChoice}
    }));
  };

  const updateDescriptorName = (e) => {
    const descriptorName = e.target.value;
    setEditor((editor) => ({...editor, descriptorName: descriptorName}));
  };

  const onKeyUp = (e) => {
    if ( e.key === 'Enter' )
      addChoice();
  };

  const mainStyle = {
    backgroundColor:ColorConstants.ULTRA_LIGHT_GREY,
    padding:'10px',
    width: "350px"
  };

  const inputStyle={marginLeft:"50px", marginRight:"10px", marginTop:"10px"};

  const nonDeletedChoices = editor.currentChoicesIndices.filter((choiceID) => {
    return !editor.deleteChoices.includes(choiceID);
  });

  const options = nonDeletedChoices.map((choiceIndex, index) => {
    const choice = editor.currentChoices[choiceIndex];
    return <div key={index}>
      <input style={inputStyle}
              key={index}
              value={choice.choice}
              onChange={(e)=>updateCurrentChoiceString(e, choice.id)}
              placeholder={`Option ${index + 1}`}
              onKeyUp={onKeyUp}
              />
            <CloseIcon data-testid="CloseIcon" style={{ fontSize: 16 }} onClick={(e)=>deleteChoice(e, choice.id)}/>
    </div>
  });

  const newOptions = editor.newChoicesIndices.map((choiceIndex, index) =>{
    const choice = editor.newChoices[choiceIndex];
    return <div key={index}>
      <input style={inputStyle}
              key={index}
              value={choice.choice}
              onChange={(e)=>updateNewChoiceString(e, choice.id)}
              autoFocus={editor.descriptorName.length > 0}
              placeholder={`Option ${options.length + index + 1}`}
              onKeyUp={onKeyUp}/>
            <CloseIcon data-testid="CloseIcon" style={{ fontSize: 16 }} onClick={(e)=>deleteNewChoice(e, choice.id)}/>
    </div>
  });

  return (
    <div>
      <div style={mainStyle}>
        <div style={{float:"left"}}>
          <input value={editor.descriptorName}
                  onChange={updateDescriptorName}
                  placeholder="Descriptor Name"
                  autoFocus={editor.descriptorName && editor.descriptorName.length == 0}/>
          <div>
            {options}
            {newOptions}
            <div>
              <Button variant="link" style={inputStyle} onClick={addChoice} disabled={disabled}>
                + Add Option
              </Button>
            </div>
          </div>
        </div>

        <div style={{float:"right", paddingLeft:"10px"}}>
          <div style={{clear:"both"}}>
            <Button size="small" variant="red" onClick={deleteClicked} block disabled={disabled}>Delete</Button>
            <Button size="small" variant="blue" onClick={doneClicked} block disabled={disabled}>Done</Button>
          </div>
        </div>

        <div style={{clear:"both"}}/>
      </div>
    </div>
  );
}

export default EditDescriptorComponent;
